import {
  AddLocationAlt,
  Directions,
  Edit,
  LocalOffer,
  Phone,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import ReactStars from "react-stars";
import MapContext from "../../Contexts/MapContext";
import UserContext, { RESOURCES } from "../../Contexts/UserContext";
import {
  getBuyOnlineUrl,
  getDeliveryEntitiesForStores,
} from "../../Services/Data.service";
import { getPrimaryButtonColors } from "../../Utils/pdpUtils";
import {
  getChainBuyUrlOverride,
  getStoreName,
  mapsSelector,
  validateSchedule,
} from "../../Utils/Utils";

function PdpActionButtons({ res, filters, storeLocatorConfig }) {
  const { deliveryInventory } = useContext(MapContext);
  const [deliveryEntities, setDeliveryEntities] = useState([]);
  const [linkBuy, setLinkBuy] = useState(null);

  const showCall = !!storeLocatorConfig?.storeDetails?.contactInfo;
  const showShop = !!storeLocatorConfig?.storeDetails?.buyOnlineLink;
  const showDirections = !!storeLocatorConfig?.storeDetails?.directionsButton;

  useEffect(() => {
    if (!res?.id) {
      setDeliveryEntities([]);
      return;
    }
    if (res?.delivery_entities?.length) {
      setDeliveryEntities(res.delivery_entities);
    } else {
      getDeliveryEntitiesForStores([res.id]).then((response) => {
        setDeliveryEntities(response || []);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res?.id]);

  useEffect(() => {
    if (!showShop || !res?.id) {
      return;
    }
    const override = getChainBuyUrlOverride(storeLocatorConfig, res);
    if (override) {
      setLinkBuy(override);
      return;
    }
    const product_id = filters?.product?.[0];
    getBuyOnlineUrl(res.id, product_id)
      .then((response) => {
        setLinkBuy(response?.buy_online_url || res.link_buy);
      })
      .catch(() => {
        setLinkBuy(res.link_buy);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, res?.id, showShop, storeLocatorConfig]);

  const storeInventory = deliveryInventory?.filter((i) =>
    (deliveryEntities || [])
      .map((de) => de.delivery_entity_uid)
      .includes(i.delivery_entity_uid),
  );
  const deliveryEntityPhone = storeInventory?.find(
    (i) => !!i?.delivery_entity?.phone,
  )?.delivery_entity?.phone;
  const phone = res.phone || deliveryEntityPhone;

  const pillSx = {
    borderRadius: 999,
    textTransform: "none",
    boxShadow: "none",
    paddingX: 1,
    flex: 1,
    flexShrink: 0,
    whiteSpace: "nowrap",
    "& .MuiButton-startIcon": { marginRight: "4px" },
    "&:hover": { boxShadow: "none" },
  };

  const neutralPillSx = {
    ...pillSx,
    backgroundColor: "#f0f0f0",
    color: "#595959",
    "&:hover": { backgroundColor: "#e4e4e4", boxShadow: "none" },
  };

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      useFlexGap
      sx={{ width: "100%" }}
    >
      {showCall && phone && (
        <Button
          size="small"
          startIcon={<Phone />}
          sx={neutralPillSx}
          onClick={(e) => {
            e.stopPropagation();
            window.open(`tel:${phone}`);
          }}
        >
          Call
        </Button>
      )}
      {showShop && linkBuy && (
        <Button
          className="store-buy-online-btn"
          id={`${res.id}`}
          size="small"
          startIcon={<ShoppingCartOutlined />}
          sx={neutralPillSx}
          onClick={(e) => {
            e.stopPropagation();
            window.open(linkBuy);
          }}
        >
          Shop
        </Button>
      )}
      {showDirections && (
        <Button
          size="small"
          variant="contained"
          color="primary"
          endIcon={<Directions />}
          sx={(theme) => {
            const colors = getPrimaryButtonColors(theme);
            return {
              ...pillSx,
              ...colors,
              minWidth: "6rem",
              "& .MuiButton-endIcon": { marginLeft: "4px" },
              "&:hover": { ...pillSx["&:hover"], ...colors },
            };
          }}
          onClick={(e) => {
            e.stopPropagation();
            mapsSelector(res.latitude, res.longitude);
          }}
        >
          Directions
        </Button>
      )}
    </Stack>
  );
}

function ResultItemPDP({
  filters,
  storeLocatorConfig,
  index,
  res,
  showResultDetailsPage,
  orgProperties,
}) {
  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const [opening_hours, setOpening_hours] = useState(null);
  const [loading_opening_hours, setLoading_opening_hours] = useState(false);

  useEffect(() => {
    const searchInGooglePlaces = async (data, accessToken) => {
      if (res?.json_content?.geolocation?.opening_hours) {
        setOpening_hours(res?.json_content?.geolocation?.opening_hours);
        setLoading_opening_hours(false);
      }
      return;
    };

    searchInGooglePlaces(res.id, userContext.state?.accessToken);
  }, [res, userContext.state]);

  const { hoverInfo, highlight_result, setHoverResultItem } = mapContext;
  const open_now = opening_hours?.open_now;
  const todayValid = validateSchedule(
    res?.start_date,
    res?.end_date,
    res?.recurrence_rule,
  );
  const featuredContent =
    !!res?.featured_content &&
    (typeof res?.featured_content === "string"
      ? JSON.parse(res?.featured_content)
      : res?.featured_content || []
    ).filter((fc) => {
      if (fc.startDate || fc.endDate || fc.recurringRule) {
        const todayValid = validateSchedule(
          fc.startDate,
          fc.endDate,
          fc.recurringRule,
        );
        return todayValid;
      }
      return true;
    });
  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  const stripAddress = (store) => {
    const { address, city, state, zipcode } = store;
    const addressRegex = new RegExp(`,|${city}|${state}|${zipcode}`, "gi");
    return address.replace(addressRegex, "").replace(/\s+/g, " ").trim();
  };

  return (
    <Paper
      style={{ width: "100%", height: "100%", borderRadius: 16 }}
      key={index}
      elevation={
        (hoverInfo?.object || highlight_result?.data)?.id === res.id ? 3 : 1
      }
      onClick={(event) => {
        showResultDetailsPage?.(
          res,
          event,
          undefined,
          index,
          opening_hours,
          filters,
        );
        if (isMobile) setHoverResultItem(res);
      }}
      onMouseOver={
        !isMobile
          ? () => {
              setHoverResultItem(res);
            }
          : undefined
      }
      onMouseOut={
        !isMobile
          ? () => {
              setHoverResultItem(undefined);
            }
          : undefined
      }
    >
      <CardContent
        style={{
          height: "100%",
          paddingBottom: "16px",
          position: "relative",
        }}
      >
        <Stack
          direction="column"
          width="100%"
          height="100%"
          alignItems="flex-start"
          spacing={0.5}
        >
          <Stack
            direction="row"
            width="100%"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography
              variant="h6"
              style={{
                lineHeight: "1.3em",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                fontWeight: 700,
                maxWidth: "100%",
              }}
            >
              {userContext?.formatText(
                getStoreName(
                  res,
                  storeLocatorConfig,
                  orgProperties?.noChainNameReplacement,
                ),
              )}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {!loading_opening_hours &&
                typeof open_now === "boolean" &&
                storeLocatorConfig?.storeDetails?.openInfo && (
                  <Typography
                    variant="caption"
                    style={{
                      color: open_now ? "#1f8a3b" : "#c62828",
                      backgroundColor: open_now ? "#e7f6ec" : "#fdeaea",
                      borderRadius: 8,
                      padding: "2px 10px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {open_now ? "Open" : "Closed"}
                  </Typography>
                )}
              {loading_opening_hours && (
                <CircularProgress size={18} color={"primary"} />
              )}
              {res?.start_date && res?.end_date && (
                <Tooltip title="Scheduled location">
                  <AddLocationAlt color={todayValid ? "success" : undefined} />
                </Tooltip>
              )}
              {userContext.checkAdminPermission(RESOURCES.STORES_UPDATE) && (
                <Tooltip title="Edit store">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.parent.postMessage(
                        { action: "edit-store", data: res },
                        "*",
                      );
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          {storeLocatorConfig?.storeDetails?.showType && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{
                lineHeight: "1.5em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {res.type}
            </Typography>
          )}
          {storeLocatorConfig?.storeDetails?.showAddress && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                lineHeight: "1.4em",
                overflow: "hidden",
                maxWidth: "100%",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {stripAddress(res)}{" "}
              {[res.city, res.state].filter((c) => !!c).join(", ")}{" "}
              {res.zipcode || ""}
            </Typography>
          )}
          {storeLocatorConfig?.storeDetails?.showReviews && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              <ReactStars
                count={5}
                edit={false}
                value={
                  ((res.yelp_rating || 0) * (res.yelp_review_count || 0) +
                    (res.google_place_rating || 0) *
                      (res.google_place_review_count || 0)) /
                  ((res.yelp_review_count || 0) +
                    (res.google_place_review_count || 0))
                }
              />
              <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{ marginLeft: 5 }}
              >
                (
                {(res.yelp_review_count || 0) +
                  (res.google_place_review_count || 0)}
                )
              </Typography>
            </div>
          )}
          {!!featuredContent?.length && (
            <Typography
              variant="caption"
              color="green"
              style={{
                lineHeight: "1.5em",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              <LocalOffer fontSize="small" />{" "}
              {featuredContent[0].title
                ? `Offer: ${featuredContent[0].title}`
                : ""}
            </Typography>
          )}
          <Box flex={1} minHeight={8} />
          <PdpActionButtons
            res={res}
            filters={filters}
            storeLocatorConfig={storeLocatorConfig}
          />
        </Stack>
      </CardContent>
    </Paper>
  );
}

export default ResultItemPDP;
